/**
 * Multi-resolution PNG export (SPEC §6.2).
 *
 * Pipeline:
 *   1. Compute target pixel size from (aspect, preset).
 *      - FHD   → short edge 1080
 *      - 4K    → short edge 2160
 *      - Print → short edge 3543 (≈ 300dpi × 30cm)
 *   2. Render the WebGL scene off-screen at the target size (RenderAdapter).
 *   3. Render the typography overlay onto a fresh 2D canvas at the SAME
 *      target size (TypographyAdapter) — NOT by upscaling the on-screen
 *      DOM/canvas. This preserves text crispness at print resolutions.
 *   4. Composite text-on-WebGL into a single 2D canvas, then `toBlob` PNG.
 *   5. Build the SPEC §6.2 filename and hand the caller `{ blob, filename }`.
 *      A `downloadBlob` helper is exported for convenience.
 *
 * The render and typography adapters run in parallel via `Promise.all` so
 * the heavier WebGL render can overlap with text rasterisation.
 *
 * Wave 3 boundary: this module owns NO direct dependency on Three.js or
 * the typography compose function. Wave 4 supplies adapters in `app.ts`.
 *
 * No `Math.random()` — everything deterministic from inputs.
 */

import type {
  AspectId,
  ChannelId,
  ResolutionPreset,
  TextLayout,
  TitleOrientation,
} from '../types.js';

/** ---------- Adapters ---------- */

export interface RenderAdapter {
  /** Render the current scene at the requested pixel size into a canvas. */
  renderToCanvas(width: number, height: number): Promise<HTMLCanvasElement>;
}

export interface TypographyAdapter {
  /**
   * Render the title/date/time text per `layout` onto a 2D canvas at the
   * given pixel dimensions. Returns a transparent text-only canvas.
   * `layout` is the result of `pickCorners(seed, aspect)`.
   */
  renderToCanvas(opts: {
    width: number;
    height: number;
    layout: TextLayout;
    title: string;
    date: string;
    time: string;
    titleSizePx?: number;
    titleWeight?: number;
    orientation?: TitleOrientation;
  }): Promise<HTMLCanvasElement>;
}

/** ---------- Public API ---------- */

export interface PngExportOpts {
  aspect: AspectId;
  channel: ChannelId;
  seed: string;
  resolution: ResolutionPreset;
  layout: TextLayout;
  title: string;
  date: string;
  time: string;
  titleSizePx?: number;
  titleWeight?: number;
  orientation?: TitleOrientation;
}

export interface PngExporter {
  export(opts: PngExportOpts): Promise<{ blob: Blob; filename: string }>;
}

/**
 * Aspect ratio numerator/denominator (matches `aspect.ts` but kept local
 * to avoid an inter-module dependency on layout code).
 */
function aspectWH(aspect: AspectId): { w: number; h: number } {
  switch (aspect) {
    case '16x9':
      return { w: 16, h: 9 };
    case '9x16':
      return { w: 9, h: 16 };
    case '4x5':
      return { w: 4, h: 5 };
    case 'ipad11_portrait':
      // iPad Pro 11" (M2): 1668x2388
      return { w: 1668, h: 2388 };
    case 'ipad13_portrait':
      // iPad Pro 12.9" (M2): 2048x2732
      return { w: 2048, h: 2732 };
  }
}

/** SPEC §6.2: short-edge target in pixels per preset. */
function shortEdgeForPreset(preset: ResolutionPreset): number {
  switch (preset) {
    case 'fhd':
      return 1080;
    case '4k':
      return 2160;
    case 'print':
      return 3543;
  }
}

/**
 * Pure: compute target pixel dimensions for an aspect/preset pair.
 *
 * The preset's "short edge" is mapped to the shorter of the two output
 * dimensions. The longer dimension is computed by scaling against the
 * aspect ratio and rounded down to an integer (consistent with
 * `computeViewport` rounding).
 */
export function computeTargetSize(
  aspect: AspectId,
  preset: ResolutionPreset,
): { width: number; height: number } {
  const { w: aw, h: ah } = aspectWH(aspect);
  const shortEdge = shortEdgeForPreset(preset);
  if (aw >= ah) {
    // landscape (or square) — short edge is height
    const height = shortEdge;
    const width = Math.floor((shortEdge * aw) / ah);
    return { width, height };
  }
  // portrait — short edge is width
  const width = shortEdge;
  const height = Math.floor((shortEdge * ah) / aw);
  return { width, height };
}

/**
 * SPEC §6.2 filename: `kanawatsugi_<aspect>_<channel>_<seed>_<preset>.png`.
 * SPEC example: `kanawatsugi_16x9_ch2_a4f3b9_4K.png` — note the preset
 * suffix is upper-cased (`FHD`, `4K`, `Print`) while the aspect/channel/
 * seed are kept lowercase. We mirror that convention exactly.
 */
const PRESET_SUFFIX: Record<ResolutionPreset, string> = {
  fhd: 'FHD',
  '4k': '4K',
  print: 'Print',
};

/**
 * Filename token per aspect. Most aspects use their AspectId verbatim;
 * the iPad portrait ids are remapped to short tokens to keep filenames
 * compact and consistent with SPEC §6.2 lowercase convention.
 */
const ASPECT_FILENAME_TOKEN: Record<AspectId, string> = {
  '16x9': '16x9',
  '9x16': '9x16',
  '4x5': '4x5',
  ipad11_portrait: 'ipad11p',
  ipad13_portrait: 'ipad13p',
};

export function buildPngFilename(
  aspect: AspectId,
  channel: ChannelId,
  seed: string,
  preset: ResolutionPreset,
): string {
  return `kanawatsugi_${ASPECT_FILENAME_TOKEN[aspect]}_${channel}_${seed}_${PRESET_SUFFIX[preset]}.png`;
}

/**
 * Trigger a browser download for a Blob. Creates an `<a>` element,
 * clicks it, and revokes the object URL on the next macrotask.
 *
 * Falls through silently in non-DOM environments (typecheck friendly).
 */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Defer revoke so the browser has a chance to start the download.
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

/** ---------- Composition ---------- */

/**
 * Composite a text canvas on top of a WebGL canvas at `(width, height)`.
 * Both inputs are expected to already be at the target resolution; we
 * draw 1:1 to avoid any interpolation.
 */
function compositeToCanvas(
  webgl: HTMLCanvasElement,
  text: HTMLCanvasElement,
  width: number,
  height: number,
): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d');
  if (ctx === null) {
    throw new Error('PNG export: failed to acquire 2D context for composite canvas.');
  }
  ctx.drawImage(webgl, 0, 0, width, height);
  ctx.drawImage(text, 0, 0, width, height);
  return out;
}

/** Promisified `canvas.toBlob('image/png')`. Rejects if the browser refuses. */
function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) {
        reject(new Error('PNG export: canvas.toBlob returned null.'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

/** ---------- Factory ---------- */

export function createPngExporter(deps: {
  render: RenderAdapter;
  typography: TypographyAdapter;
}): PngExporter {
  return {
    async export(opts: PngExportOpts): Promise<{ blob: Blob; filename: string }> {
      const { width, height } = computeTargetSize(opts.aspect, opts.resolution);

      // Run WebGL render and typography rasterisation in parallel — the
      // heavy WebGL frame can overlap with 2D text rendering.
      const [webgl, text] = await Promise.all([
        deps.render.renderToCanvas(width, height),
        deps.typography.renderToCanvas({
          width,
          height,
          layout: opts.layout,
          title: opts.title,
          date: opts.date,
          time: opts.time,
          titleSizePx: opts.titleSizePx,
          titleWeight: opts.titleWeight,
          orientation: opts.orientation,
        }),
      ]);

      const composed = compositeToCanvas(webgl, text, width, height);
      const blob = await canvasToPngBlob(composed);
      const filename = buildPngFilename(opts.aspect, opts.channel, opts.seed, opts.resolution);
      return { blob, filename };
    },
  };
}
